import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Trash, ShieldWarning, CheckCircle } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const API = process.env.REACT_APP_BACKEND_URL;

export default function DeleteAccount() {
  const [form, setForm] = useState({ email: "", full_name: "", reason: "", confirm: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (form.confirm.trim().toLowerCase() !== "delete my account") {
      toast.error('Please type "delete my account" in the confirmation box.');
      return;
    }
    if (!form.email || !form.full_name) {
      toast.error("Email and full name are required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/account/delete-request`, {
        email: form.email.trim(),
        full_name: form.full_name.trim(),
        reason: form.reason.trim() || null,
      });
      setDone({ ref: data.reference, submitted_at: data.submitted_at });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please email privacy@zenith360-hr.com instead.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16" data-testid="delete-account-done">
        <div className="border border-emerald-300 bg-emerald-50 rounded-sm p-6 text-center">
          <CheckCircle size={40} weight="fill" className="mx-auto text-emerald-700" />
          <h1 className="font-display text-3xl font-bold text-emerald-900 mt-2">Deletion request received</h1>
          <p className="text-sm text-emerald-800 mt-3">
            We’ve queued your request. Reference number: <b>{done.ref}</b>.
          </p>
          <p className="text-sm text-zinc-700 mt-4">
            Our privacy team will process it within <b>7 business days</b>. You’ll receive a confirmation email at{" "}
            <b>{form.email}</b> once every data point tied to your account has been erased.
          </p>
          <p className="text-xs text-zinc-500 mt-4">
            If your account is managed by an employer / agency, your controller (them) will be notified in parallel
            per our{" "}
            <Link to="/privacy" className="text-adb-navy underline">Privacy Policy</Link>.
          </p>
          <Link to="/" className="mt-6 inline-block text-xs uppercase tracking-widest text-adb-navy font-bold">← Back to homepage</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-16" data-testid="delete-account-page">
      <Link to="/" className="text-xs text-zenith-teal font-bold uppercase tracking-widest">← Back to Zenith360 HR</Link>
      <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-adb-navy mt-4">Delete my account</h1>
      <p className="text-sm text-zinc-500 mt-2">Zenith360 HR · Data-deletion request form</p>

      <div className="mt-8 space-y-4 text-[15px] leading-relaxed text-zinc-700">
        <p>
          You’re free to close your account and ask us to erase your personal data at any time. Submit the form
          below and our privacy team will process the request within <b>7 business days</b>.
        </p>

        <div className="border border-amber-300 bg-amber-50 rounded-sm p-4 flex items-start gap-3">
          <ShieldWarning size={20} weight="fill" className="text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <div className="font-bold">What will be deleted</div>
            <ul className="list-disc pl-5 mt-1 space-y-0.5 text-[13px]">
              <li>Your Zenith360 login (email + hashed password)</li>
              <li>Your baseline selfie and every stored check-in photo</li>
              <li>Your GPS trip history + waypoints</li>
              <li>Your uploaded documents (ID proofs, letters, receipts)</li>
              <li>Your in-app messages, kudos and feedback</li>
              <li>Your device push tokens</li>
            </ul>
            <div className="font-bold mt-2">What is legally retained (anonymised)</div>
            <ul className="list-disc pl-5 mt-1 space-y-0.5 text-[13px]">
              <li>Payroll records — retained anonymised for 7 years (India tax law)</li>
              <li>Statutory-compliance filings (PF / ESIC / TDS) — retained per statute</li>
              <li>Audit logs of your actions — retained anonymised for 3 years</li>
            </ul>
          </div>
        </div>

        <p className="text-xs text-zinc-500">
          If your account is managed by your employer (organisation, group, or agency), the account may still exist
          under their control — but every personal identifier that ties the record back to you will be scrubbed. If
          you prefer to keep the account and only opt-out of specific features, contact your HR team directly.
        </p>
      </div>

      <form onSubmit={submit} className="mt-8 space-y-4 border border-zinc-200 rounded-sm bg-white p-6">
        <div>
          <Label className="text-xs font-bold">Full name *</Label>
          <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="As on your Zenith360 profile" className="rounded-sm mt-1" data-testid="del-name" />
        </div>
        <div>
          <Label className="text-xs font-bold">Email address on file *</Label>
          <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" className="rounded-sm mt-1" data-testid="del-email" />
          <div className="text-xs text-zinc-500 mt-1">The email your Zenith360 account uses to log in.</div>
        </div>
        <div>
          <Label className="text-xs font-bold">Reason (optional)</Label>
          <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Left the company · switched product · no longer needed · etc." className="rounded-sm mt-1" rows={3} data-testid="del-reason" />
          <div className="text-xs text-zinc-500 mt-1">Helps us improve — completely optional.</div>
        </div>
        <div>
          <Label className="text-xs font-bold">Confirmation *</Label>
          <Input required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} placeholder='Type: delete my account' className="rounded-sm mt-1" data-testid="del-confirm" />
          <div className="text-xs text-zinc-500 mt-1">To prevent accidents, please type <b>delete my account</b> exactly.</div>
        </div>
        <Button type="submit" disabled={submitting} className="w-full bg-red-600 hover:bg-red-700 text-white rounded-sm h-11" data-testid="del-submit">
          <Trash size={16} weight="fill" className="mr-2" /> {submitting ? "Submitting…" : "Submit deletion request"}
        </Button>
        <div className="text-xs text-zinc-500 text-center">
          Prefer email? Send your request to{" "}
          <a href="mailto:privacy@zenith360-hr.com" className="text-adb-navy underline">privacy@zenith360-hr.com</a>.
        </div>
      </form>
    </div>
  );
}
